import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'api/v1/restaurants/users';
export const getRestaurantUsersEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;

type TQueryKey = [
  string,
  {
    page: TParam;
    limit: TParam;
    name: TParam;
    email: TParam;
    phoneNumber: TParam;
    roleId: TParam;
    subEntityId: TParam;
    subEntityType: TParam;
    sort: TParam;
  }
];

type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetRestaurantUsersApi(restaurantId: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name, email, phoneNumber, roleId, subEntityId, subEntityType, sort } = getPageParams();
  const fullEndpoint = `${endpoint}/${restaurantId}`;

  const queryFn = async () => {
    try {
      const { data } = await http.get(fullEndpoint, {
        params: { page, limit, name, email, phoneNumber, roleId, subEntityId, subEntityType, sort },
      });

      return data;
    } catch (error: any) {
      if (!error.response.isActive || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [fullEndpoint, { page, limit, name, email, phoneNumber, roleId, subEntityId, subEntityType, sort }],
    queryFn,
    {
      enabled: isLoggedIn,
      ...options,
    }
  );
}
